import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { safeJsonParse } from "@utils/safeJsonParse";

/** 설정을 아직 한 번도 해석하지 못한 상태. logoSrc 가 undefined 라 Logo 는 로고 자리를 비워 둔다. */
const UNRESOLVED = { headerTheme: null, logoSrc: undefined };

/** 설정 조회가 실패해 해석할 값이 영영 오지 않는 상태. 빈 자리를 남기지 않도록 기본 로고를 그린다. */
const FAILED = { headerTheme: { ci: null }, logoSrc: null };

/**
 * 헤더 커스텀 테마(고객 CI 로고·헤더 색상)를 설정 응답에서 읽는다.
 *
 * 예전에는 이 값을 localStorage(headerTheme / projectHeaderTheme)에 복사해 두고 헤더가 그것을 읽었다.
 * 그런데 읽는 시점 자체가 설정 응답 이후라 선점 렌더 이득은 없으면서, 설정을 다시 받아온 화면에서도
 * 헤더는 마지막으로 "기록된" 값을 다시 적용해 오래된 값(만료된 presigned CI URL 등)이 남았다. [DAM-821]
 * 그래서 redux 의 설정 응답을 단일 소스로 쓴다.
 *
 * 스페이스/프로젝트 두 계층을 클라이언트에서 합성하지 않는다 — 프로젝트 설정 응답이 스페이스 값을
 * 상속·오버라이드해 내려주므로(스페이스에서 isOverridable 이 false 면 스페이스 값이 그대로 온다)
 * 현재 계층의 값이 곧 최종값이다.
 *
 * @param {Object} params
 * @param {string|null} [params.projectId] - 프로젝트 컨텍스트면 프로젝트 설정, 없으면 스페이스 설정을 본다.
 * @returns {{headerTheme: Object|null, logoSrc: string|null|undefined}}
 *   headerTheme 은 해석 전이면서 조회 실패도 아닐 때만 null 이고, 그 외에는 항상 객체다.
 *   logoSrc 는 세 상태를 구분한다 — undefined(첫 해석 전) / null(커스텀 로고 없음·조회 실패) / URL.
 */
const useHeaderTheme = ({ projectId } = {}) => {
    // settings 슬라이스 전체를 구독하면 로고와 무관한 설정 액션마다 헤더가 리렌더된다.
    const projectGeneral = useSelector(({ settings }) => settings?.project?.General);
    const stageSettings = useSelector(({ settings }) => settings?.stage);
    const settingsError = useSelector(({ settings }) => settings?.error);

    const resolved = useMemo(() => {
        // 응답 전에 기본 로고를 먼저 그리면 곧 고객 로고로 바뀌며 깜빡인다. 그동안은 로고 자리를 비워 둔다.
        // 프로젝트 설정은 resource 단위로 도착하므로 General 이 들어왔는지로 판단한다.
        const isResolved = projectId ? !!projectGeneral : !!stageSettings;
        if (!isResolved) return null;

        const general = projectId ? projectGeneral : stageSettings.General;
        const value = safeJsonParse(general?.headerTheme?.value);
        // isDefault 는 커스텀 색상을 쓰지 않는다는 표시다. 로고(ci)는 그대로 둔다.
        const headerTheme = value ? (value.isDefault ? { ...value, color: null } : value) : { ci: null };

        return { headerTheme, logoSrc: headerTheme.ci || null };
    }, [projectId, projectGeneral, stageSettings]);

    // 설정이 비워져도 마지막으로 해석한 값으로 버틴다. 헤더는 레이아웃 소속이라 같은 레이아웃
    // 컴포넌트 안에서는 라우트가 바뀌어도 마운트를 유지하는데, 관리자 설정 화면 이탈 시 settings 가
    // reset 되면 로고와 헤더 색상이 새로고침 전까지 사라지기 때문이다.
    // (이전에는 각 헤더의 로컬 state 가 같은 역할을 했다)
    // NOTE: 아래 두 ref 를 렌더 단계에서 대입한다. React 17(legacy 모드)에서는 안전하나,
    //       concurrent 모드로 올릴 때는 재검토해야 한다.
    const lastResolvedRef = useRef(null);
    if (resolved) lastResolvedRef.current = resolved;

    // settings.error 는 뒤따르는 settings 액션 하나로 지워지는 공유 플래그다(모든 PENDING·SUCCESS 가
    // false 로 되돌린다). 프로젝트 진입 시 MenuName 과 General 을 동시에 쏘므로(routes/providers/user.js)
    // General 만 실패하면 뒤늦게 도착한 MenuName 응답이 플래그를 지워, 실패를 그 순간에 보지 않으면 놓친다.
    // 그래서 한 번 본 실패는 붙잡아 두고, 정작 필요한 값이 도착하면(resolved) 푼다. [DAM-821]
    //
    // 알려진 한계: 이 latch 는 ref 라 훅 인스턴스 수명에 묶인다. 실패가 헤더 없는 화면에서 지나가면
    // (App.js 는 pathname 으로 레이아웃 컴포넌트 타입을 갈아끼우고, "/" 진입 시 쓰는
    // LayoutForOnlyComponent 는 헤더를 렌더하지 않는다) 관측할 인스턴스가 없어 놓친다.
    // 그때는 기존과 같이 로고 자리가 빈 채로 남는다 — 전제가 General 조회 실패라 드문 경우로 보고 남겨 둔다.
    const sawErrorRef = useRef(false);
    if (settingsError) sawErrorRef.current = true;
    if (resolved) sawErrorRef.current = false;

    // 조회가 실패하면(404 가 아닌 500·네트워크 오류) 리듀서가 stage/project 를 채우지 않아 영영 해석되지
    // 않고, 재조회 트리거도 없어 로고 자리가 빈 채로 굳는다. 그때는 기본 로고로 대체한다. [DAM-821]
    // 마지막으로 해석한 값이 있으면 그쪽이 우선이다 — 실패했다고 이미 그리던 고객 로고를 내리지 않는다.
    return resolved ?? lastResolvedRef.current ?? (sawErrorRef.current ? FAILED : UNRESOLVED);
};

export default useHeaderTheme;
